import { Injectable } from '@angular/core';
import { UploadActions } from '@app/client/uploader/store/actions';
import * as fromUploader from '@app/client/uploader/store/reducers';
import * as fromRoot from '@app/reducers';
import { FileDropEntry } from '@app/shared/components/smash-file-drop/file-drop-entry';
import { FileSystemMetadata } from '@app/shared/components/smash-file-drop/interfaces';
import { Identity } from '@app/shared/models/identity';
import { Store } from '@ngrx/store';

@Injectable({
  providedIn: 'root'
})
export class RawFileStoreService {

  constructor(private readonly store$: Store<fromRoot.State & fromUploader.State>) { }

  private files: { [key: string]: FileDropEntry } = {};
  private errors: { subject: any, reason: string }[] = [];
  private forbiddenExtensionsFreemium = ['htm', 'html', 'mkv'];

  public removeFile(name: string) {
    const fileMeta = this.getFileMetadata(this.files[name]);
    delete this.files[name];
    this.store$.dispatch(UploadActions.RemoveRawFile({ file: fileMeta }));
  }

  public getFolderFiles(name: string): FileDropEntry[] {
    const folderFiles = [];
    Object.entries(this.files).forEach(([key, value]) => {
      if (key.startsWith(name + '/')) {
        folderFiles.push(value);
      }
    });
    return folderFiles;
  }

  public removeFolder(name: string) {
    const folderFiles = this.getFolderFiles(name);
    const filesDeleted = [];
    for (const file of folderFiles) {
      const fileMeta = this.getFileMetadata(this.files[file.relativePath]);
      delete this.files[file.relativePath];
      filesDeleted.push(fileMeta);
    }
    this.store$.dispatch(UploadActions.RemoveRawFiles({ files: filesDeleted }));
  }

  public getFileFullName(file: FileDropEntry) {
    return file.relativePath;
  }

  public getFileRootFolder(file: FileDropEntry) {
    return file.relativePath ? file.relativePath.split('/')[0] : null;
  }

  public setFile(file: FileDropEntry) {
    return this.files[file.relativePath] = file;
  }

  public getFileMetadata(file: FileDropEntry): FileSystemMetadata {
    const metadata = {
      name: file.fileEntry.name,
      size: file.size,
      rootFolder: file.rootFolder,
      isDirectory: file.fileEntry.isDirectory,
      isFile: file.fileEntry.isFile,
      relativePath: file.relativePath,
      extension: file.extension,
    };
    return metadata;
  }

  public getFile(name: string): FileDropEntry {
    return this.files[name];
  }

  public getFiles(): FileDropEntry[] {
    return Object.values(this.files);
  }

  public async setFiles(files: FileDropEntry[], identity?: Identity): Promise<FileSystemMetadata[]> {
    this.resetErrors();
    const newFiles: FileDropEntry[] = [];
    for (const file of files) {

      try {
        await file.buildMetaFromFile();
      } catch (error: unknown) {
        // A filesystem error reading this one entry (too-long path, cloud-sync placeholder,
        // permission glitch) must not silently hang the whole batch -- skip just this file and
        // keep processing the rest, instead of the sequential loop stalling on it forever.
        // Reported as RELATIVEPATH_TOO_LONG, the same message a file that merely comes through
        // as 0 bytes below already shows -- both are the same "this file could not be properly
        // imported" case from the user's point of view, and folder-select (which never rejects
        // here, see smash-file-drop.component.ts's fakeFileEntry) only ever surfaces THIS message
        // for it.
        console.error(`[RawFileStoreService] Failed to read "${file.relativePath}":`, error);
        this.errors.push({ subject: file.relativePath, reason: 'RELATIVEPATH_TOO_LONG' });
        continue;
      }
      if (this.files[file.relativePath]) {
        this.errors.push({ subject: file.relativePath, reason: 'DUPLICATE_FILES' });
      } else if (this.isFileExtensionForbidden(file.extension, !!identity?.username)) {
        this.errors.push({ subject: file.relativePath, reason: 'FORBIDDEN_FILE_EXTENSION' });
      } else {
        if (file.size === 0) {
          // relative path is not reliable
          // just warn the user that some file may be corrupted due to absolute path 260 char limits
          this.errors.push({ subject: file.relativePath, reason: 'RELATIVEPATH_TOO_LONG' });
        }
        this.setFile(file);
        newFiles.push(file);
      }
    }

    if (this.hasDuplicateErrors()) {
      this.store$.dispatch(UploadActions.AddRawFilesFailure({ error: { type: 'DUPLICATE_FILES' } }));
    }
    if (this.hasForbiddenExtensionErrors()) {
      this.store$.dispatch(UploadActions.AddRawFilesFailure({ error: { type: 'FORBIDDEN_FILE_EXTENSION' } }));
    }
    if (this.hasRelativePathTooLongErrors()) {
      this.store$.dispatch(UploadActions.AddRawFilesFailure({ error: { type: 'RELATIVEPATH_TOO_LONG' } }));
    }

    const newFilesMetadata = newFiles.map(file => this.getFileMetadata(file));
    this.store$.dispatch(UploadActions.AddRawFilesSuccess({ files: newFilesMetadata }));
    return newFilesMetadata;
  }

  reset() {
    this.resetErrors();
    this.files = {};
  }

  /**
   * Surfaces the same RELATIVEPATH_TOO_LONG alert as setFiles() does for a discovered file that
   * comes through empty, for a file lost earlier -- to a directory-traversal failure (readEntries
   * erroring on a too-long path, a cloud-sync placeholder, a permission glitch) -- that never
   * reached setFiles() in the first place, since no FileDropEntry is ever created for it.
   * Dispatched independently of setFiles()'s own error bookkeeping so a later setFiles() call's
   * resetErrors() does not wipe it.
   */
  public reportRelativePathTooLongError() {
    this.store$.dispatch(UploadActions.AddRawFilesFailure({ error: { type: 'RELATIVEPATH_TOO_LONG' } }));
  }

  private hasDuplicateErrors() {
    return this.errors.some(error => error.reason === 'DUPLICATE_FILES');
  }

  private hasForbiddenExtensionErrors() {
    return this.errors.some(error => error.reason === 'FORBIDDEN_FILE_EXTENSION');
  }

  private hasRelativePathTooLongErrors() {
    return this.errors.some(error => error.reason === 'RELATIVEPATH_TOO_LONG');
  }

  private resetErrors() {
    this.store$.dispatch(UploadActions.ResetRawFilesError());
    this.errors.length = 0;
  }

  private isFileExtensionForbidden(extension: string, isPremium = false) {
    if (isPremium) {
      return false;
    } else {
      return this.forbiddenExtensionsFreemium.includes(extension.toLowerCase());
    }
  }
}
